import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { onboardingTokens, visuallyHidden } from "../theme";
import { t } from "../../../locales/t";
import { YOUTH_PROGRAM_URLS } from "../../../constants/externalLinks";

/**
 * Shown when someone under 16 tries to join.
 *
 * Two things this is not: it is not an error, and it is not a dead end. A child
 * interested enough in aviation to sign up is exactly who Elevate's youth
 * programmes exist for, so the gate hands them off rather than turning them
 * away. Tone throughout is warm for that reason.
 *
 * It also never advances the flow. Dismissing returns to the date step with
 * what was typed still there, so a mistyped year can be corrected without
 * starting again — and a genuine under-16 simply cannot get past it.
 *
 * Programmes open in a new tab, so the signup tab keeps its place.
 *
 * Bottom sheet on phones, centred dialog from tablet up: the same component,
 * anchored differently, so there is one set of content to keep correct.
 */

const PROGRAM_KEYS = Object.keys(YOUTH_PROGRAM_URLS);

const AgeGateDialog = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    // MUI closes on Escape and on backdrop click through onClose, which covers
    // both dismissal routes the ticket asks for.
    aria-labelledby="age-gate-heading"
    fullWidth
    maxWidth="sm"
    sx={{
      "& .MuiDialog-container": {
        alignItems: { xs: "flex-end", sm: "center" },
      },
    }}
    slotProps={{
      paper: {
        sx: {
          m: { xs: 0, sm: 4 },
          width: { xs: "100%", sm: "auto" },
          maxHeight: "92vh",
          borderRadius: { xs: "1.5rem 1.5rem 0 0", sm: "1.5rem" },
          backgroundColor: onboardingTokens.card,
        },
      },
    }}
  >
    <Box
      sx={{
        position: "relative",
        px: 3,
        pt: { xs: 3, sm: 4 },
        pb: 3.5,
        background: "linear-gradient(135deg, #012f7c 0%, #014fa8 100%)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="start">
        <Box
          aria-hidden="true"
          sx={{
            width: 48,
            height: 48,
            borderRadius: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
            backgroundColor: "rgba(1, 190, 204, 0.25)",
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: 24, color: "#7bb9d5" }} />
        </Box>

        <IconButton
          onClick={onClose}
          aria-label={t("onboarding.ageGate.close")}
          size="small"
          sx={{
            color: "rgba(255,255,255,0.7)",
            backgroundColor: "rgba(255,255,255,0.1)",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Typography
        id="age-gate-heading"
        variant="h2"
        sx={{
          fontSize: "clamp(1.375rem, 4vw, 1.75rem)",
          color: "#FFFFFF",
          lineHeight: 1.2,
          letterSpacing: "-0.02em",
          mb: 1.25,
        }}
      >
        {t("onboarding.ageGate.heading")}
      </Typography>

      <Typography
        sx={{
          fontSize: "0.875rem",
          color: "rgba(255,255,255,0.7)",
          lineHeight: 1.65,
        }}
      >
        {t("onboarding.ageGate.subheading")}
      </Typography>
    </Box>

    <Box sx={{ px: 3, py: 3 }}>
      <Typography
        sx={{
          fontSize: "0.9375rem",
          lineHeight: 1.65,
          mb: 3,
        }}
      >
        {t("onboarding.ageGate.body")}
      </Typography>

      <Box
        sx={{
          borderRadius: "0.75rem",
          overflow: "hidden",
          border: `1.5px solid ${onboardingTokens.track}`,
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            backgroundColor: onboardingTokens.lightTeal,
            borderBottom: `1px solid ${onboardingTokens.track}`,
          }}
        >
          <Typography
            component="h3"
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              color: onboardingTokens.teal,
            }}
          >
            {t("onboarding.ageGate.programsTitle")}
          </Typography>
        </Box>

        {PROGRAM_KEYS.map((key, index) => (
          // ButtonBase rather than Link: the onboarding theme gives ButtonBase
          // its visible keyboard focus outline.
          <ButtonBase
            key={key}
            component="a"
            href={YOUTH_PROGRAM_URLS[key]}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 2,
              textAlign: "left",
              color: "inherit",
              backgroundColor: onboardingTokens.card,
              borderTop:
                index > 0 ? `1px solid ${onboardingTokens.track}` : "none",
              "&:hover": { backgroundColor: onboardingTokens.lightTeal },
              // Inset: the list's rounded border clips anything drawn outside.
              "&:focus-visible": { outlineOffset: -3 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>
                {t(`onboarding.ageGate.programs.${key}.name`)}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: onboardingTokens.mutedForeground,
                  lineHeight: 1.45,
                }}
              >
                {t(`onboarding.ageGate.programs.${key}.description`)}
              </Typography>
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ ml: 2, flexShrink: 0 }}
            >
              <Chip
                size="small"
                label={t(`onboarding.ageGate.programs.${key}.ages`)}
                sx={{
                  flexShrink: 0,
                  cursor: "inherit",
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: onboardingTokens.teal,
                  backgroundColor: onboardingTokens.lightTeal,
                }}
              />
              <ArrowForwardIcon
                aria-hidden="true"
                sx={{ color: onboardingTokens.teal, fontSize: 18 }}
              />
            </Stack>

            <Box component="span" sx={visuallyHidden}>
              {t("onboarding.layout.opensInNewTab")}
            </Box>
          </ButtonBase>
        ))}
      </Box>

      <Button
        fullWidth
        onClick={onClose}
        sx={{
          py: 1.5,
          backgroundColor: onboardingTokens.muted,
          color: onboardingTokens.navy,
          fontWeight: 600,
        }}
      >
        {t("onboarding.ageGate.goBack")}
      </Button>
    </Box>
  </Dialog>
);

export default AgeGateDialog;
